import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Blog listing: button-triggered pagination (loads sections via PaginatedList).
 */
export default class BlogPostsList extends PaginatedList {
  /** @type {Set<number>} */
  #loadedPages = new Set();

  /** @type {number} */
  #minLoadedPage = 1;

  /** @type {number} */
  #maxLoadedPage = 1;

  /** @type {number} */
  #lastPage = 1;

  /** @type {boolean} */
  #isLoadingPage = false;

  connectedCallback() {
    super.connectedCallback();
    this.infinityScrollObserver?.disconnect();
    this.#initializePaginationState();
    this.#bindManualPagination();
    this.#sortLoadedCardsByLastUpdated();
  }

  disconnectedCallback() {
    this.#unbindManualPagination();
    super.disconnectedCallback();
  }

  #bindManualPagination() {
    this.refs.viewMoreButtonNext?.addEventListener('click', this.#handleNextClick);
    this.refs.viewMoreButtonPrevious?.addEventListener('click', this.#handlePreviousClick);
    this.#syncManualPagination();
  }

  #unbindManualPagination() {
    this.refs.viewMoreButtonNext?.removeEventListener('click', this.#handleNextClick);
    this.refs.viewMoreButtonPrevious?.removeEventListener('click', this.#handlePreviousClick);
  }

  #syncManualPagination() {
    const nextBtn = this.refs.viewMoreButtonNext;
    const prevBtn = this.refs.viewMoreButtonPrevious;
    if (nextBtn) nextBtn.hidden = !this.#hasNextPage;
    if (prevBtn) prevBtn.hidden = !this.#hasPreviousPage;
  }

  #handleNextClick = async () => {
    const btn = this.refs.viewMoreButtonNext;
    if (!btn || btn.hidden || !this.#hasNextPage || this.#isLoadingPage) return;
    try {
      const loadedPage = this.#maxLoadedPage + 1;
      const didLoad = await this.#loadPage(loadedPage, 'next');
      if (didLoad) this.#updateUrlPage(loadedPage);
      this.#sortLoadedCardsByLastUpdated();
    } finally {
      this.#syncManualPagination();
    }
  };

  #handlePreviousClick = async () => {
    const btn = this.refs.viewMoreButtonPrevious;
    if (!btn || btn.hidden || !this.#hasPreviousPage || this.#isLoadingPage) return;
    try {
      const loadedPage = this.#minLoadedPage - 1;
      const didLoad = await this.#loadPage(loadedPage, 'previous');
      if (didLoad) this.#updateUrlPage(loadedPage);
      this.#sortLoadedCardsByLastUpdated();
    } finally {
      this.#syncManualPagination();
    }
  };

  #initializePaginationState() {
    const grid = this.refs.grid;
    if (!grid) return;

    this.#loadedPages.clear();
    this.#lastPage = Number(grid.dataset.lastPage || 1);
    if (!Number.isFinite(this.#lastPage) || this.#lastPage < 1) {
      this.#lastPage = 1;
    }

    const cards = Array.from(grid.querySelectorAll('[ref="cards[]"]')).filter((card) => card instanceof HTMLElement);
    cards.forEach((card) => {
      const page = Number(card.dataset.page || 1);
      if (Number.isFinite(page) && page >= 1) {
        this.#loadedPages.add(page);
      }
    });

    if (this.#loadedPages.size === 0) {
      this.#loadedPages.add(1);
    }

    const loadedPages = Array.from(this.#loadedPages).sort((a, b) => a - b);
    this.#minLoadedPage = loadedPages[0] || 1;
    this.#maxLoadedPage = loadedPages[loadedPages.length - 1] || 1;
  }

  /**
   * @param {number} page
   * @param {'previous' | 'next'} direction
   * @returns {Promise<boolean>}
   */
  async #loadPage(page, direction) {
    const grid = this.refs.grid;
    if (!grid || this.#loadedPages.has(page) || page < 1 || page > this.#lastPage) return false;

    this.#setLoadingState(true);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(page));
      url.hash = '';

      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
      const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
      const pageGrid = parsedPage.querySelector('[ref="grid"]');
      if (!(pageGrid instanceof HTMLElement)) return false;

      const pageItems = Array.from(pageGrid.querySelectorAll(':scope > [ref="cards[]"]')).filter(
        (item) => item instanceof HTMLElement
      );
      if (pageItems.length === 0) return false;

      if (direction === 'next') {
        grid.append(...pageItems);
      } else {
        grid.prepend(...pageItems);
      }

      this.#loadedPages.add(page);
      this.#minLoadedPage = Math.min(this.#minLoadedPage, page);
      this.#maxLoadedPage = Math.max(this.#maxLoadedPage, page);
      return true;
    } finally {
      this.#setLoadingState(false);
    }
  }

  /**
   * @param {number} page
   */
  #updateUrlPage(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    history.pushState('', '', url.toString());
  }

  /**
   * @param {boolean} loading
   */
  #setLoadingState(loading) {
    this.#isLoadingPage = loading;
    if (this.refs.viewMoreButtonNext) this.refs.viewMoreButtonNext.disabled = loading;
    if (this.refs.viewMoreButtonPrevious) this.refs.viewMoreButtonPrevious.disabled = loading;
  }

  get #hasNextPage() {
    return this.#maxLoadedPage < this.#lastPage;
  }

  get #hasPreviousPage() {
    return this.#minLoadedPage > 1;
  }

  #sortLoadedCardsByLastUpdated() {
    const grid = this.refs.grid;
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll('[ref="cards[]"]')).filter((card) => card instanceof HTMLElement);
    if (cards.length <= 1) return;

    const latestWidgetCard = cards.find((card) => card.querySelector('.latest-posts-widget'));
    const sortableCards = cards.filter((card) => card !== latestWidgetCard);
    sortableCards.sort((a, b) => Number(b.dataset.updatedAt || 0) - Number(a.dataset.updatedAt || 0));

    if (latestWidgetCard) {
      grid.append(latestWidgetCard, ...sortableCards);
      return;
    }

    grid.append(...sortableCards);
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
